import React from "react";

import { TFunction } from "i18next";
import { camelCase } from "lodash";
import { useTranslation } from "react-i18next";
import { Box, Collapse, Table, TableBody, TableCell } from "@mui/material";
import IconButton from "@mui/material/IconButton";

import { DocumentIcon, FolderIcon, PlainArrowDown, PlainArrowRight } from "@/assets/icons";
import { DateFormat } from "@/components/atoms";
import { isNotEmpty } from "@/core/string";

import { getExpirationDateWarningColor } from "../../utils/getExpirationDateWarningColor";
import { StyledSignButton, StyledTableCell, StyledTableRow } from "./styled";
import useConnectiveUrl from "./useConnectiveUrl";

function getDocumentTitle(t: TFunction, document: MyBrokerApi.Documentt): string {
    if (isNotEmpty(document.description) && isNotEmpty(document.origin)) {
        return `${document.description} (${t("Common.Concerns")}: ${document.origin})`;
    } else if (isNotEmpty(document.filename) && isNotEmpty(document.origin)) {
        return `${document.filename} (${t("Common.Concerns")}: ${document.origin})`;
    } else if (isNotEmpty(document.description)) {
        return document.description;
    }
    return document.filename;
}

function PackageTableItem(props: { pkg: MyBrokerApi.DocumentsPackageToSign }) {
    const { pkg } = props;
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);
    const { connectiveUrlLoading, connectiveUrlClicked } = useConnectiveUrl({ pkg });
    const displayAsPackage = pkg.documentsList.length > 1;
    const expirationDateColor = getExpirationDateWarningColor(pkg.expirationDate);
    return (
        <React.Fragment>
            <StyledTableRow
                className="mainRow"
                sx={{ "& > *": { borderBottom: "unset" } }}
                data-testid={`packages_page-item-${pkg.guid}`}
            >
                <StyledTableCell component="th" scope="row" className="documentTitle" data-testid="table-cell-name">
                    {displayAsPackage ? (
                        <>
                            <IconButton
                                data-testid="icon-button-expand"
                                aria-label="expand row"
                                size="small"
                                onClick={() => setOpen(!open)}
                            >
                                {open ? <PlainArrowDown /> : <PlainArrowRight />}
                            </IconButton>
                            <FolderIcon className="typeIcon" />
                        </>
                    ) : (
                        <DocumentIcon className="typeIcon" style={{ marginLeft: 24 }} />
                    )}
                    <span>{pkg.name}</span>
                </StyledTableCell>
                <StyledTableCell data-testid="table-cell-concerns">{pkg.documentsList[0].policyHolder}</StyledTableCell>
                <StyledTableCell data-testid="table-cell-date">
                    <DateFormat date={pkg.requestDate} />
                </StyledTableCell>
                <StyledTableCell data-testid="table-cell-expirationDate" style={{ color: expirationDateColor }}>
                    <DateFormat date={pkg.expirationDate} />
                </StyledTableCell>
                <StyledTableCell style={{ textAlign: "right" }} data-testid="table-cell-button">
                    <StyledSignButton
                        disabled={connectiveUrlLoading}
                        classes={{ root: "button-root" }}
                        onClick={connectiveUrlClicked}
                        data-testid={`package_sign_button-item-${camelCase(pkg.guid)}`}
                    >
                        {t("ToSign.Confirm.Sign")}
                    </StyledSignButton>
                </StyledTableCell>
            </StyledTableRow>
            {displayAsPackage && (
                <StyledTableRow sx={{ visibility: open ? "visible" : "collapse" }}>
                    <TableCell style={{ padding: 0 }} colSpan={5}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box>
                                <Table size="small" aria-label="">
                                    <TableBody>
                                        {pkg.documentsList.map((document) => (
                                            <StyledTableRow className="secondaryRow" key={document.id}>
                                                <StyledTableCell component="th" scope="row">
                                                    <DocumentIcon className="typeIcon" />
                                                    <span>{getDocumentTitle(t, document)}</span>
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </StyledTableRow>
            )}
        </React.Fragment>
    );
}

export default PackageTableItem;
